import clsx, { type ClassValue } from 'clsx';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { twMerge } from 'tailwind-merge';

import { FEATURE_NAMES, IPlanModal, PLAN_TYPE, TFEATURE_NAMES } from '@/types';

import { FileDriverType, FileDriverValues } from '../types/common';

import { countryFlags, FEATURE_DISPLAY_NAMES, SOUND_OPTION_LIST } from './constants';

dayjs.extend(relativeTime);

export const cn = (...args: ClassValue[]) => twMerge(clsx(args));

export const formatDateStr = (dateStr: string, isYear = true) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: '2-digit',
    year: isYear ? 'numeric' : undefined,
  });
};

export const getProfileName = (name: string) => {
  const words = name.trim().split(' ').filter(Boolean);
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  return words
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('');
};

export const formatFullDateStr = (dateStr: string) => {
  const date = new Date(dateStr);
  return date.toString().replace(/\s\(.*\)$/, '');
};

export const formatNumber = (num: number): string => {
  if (num >= 1000000000) {
    const billion = num / 1000000000;
    return `${billion % 1 === 0 ? billion : billion.toFixed(1)}B`;
  }
  if (num >= 1000000) {
    const million = num / 1000000;
    return `${million % 1 === 0 ? million : million.toFixed(1)}M`;
  }
  if (num >= 1000) {
    const thousand = num / 1000;
    return `${thousand % 1 === 0 ? thousand : thousand.toFixed(1)}K`;
  }
  return num.toString();
};

export const getFileUrl = (key?: string, driver?: FileDriverType) => {
  if (!key) return;

  if (driver === FileDriverValues.EXTERNAL) return key;

  return `${process.env.NEXT_PUBLIC_API_URL}/files/${key}`;
};

export const formatTime = (dateStr: string): string => {
  if (!dateStr) return '';

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';

  const formatter = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  });

  return formatter.format(date);
};

export const getDomainFromUrl = (url: string): string => {
  try {
    const { hostname } = new URL(url);
    return hostname.replace(/^www\./, '');
  } catch (error) {
    return url;
  }
};

export const getTimeAgo = (dateStr: string): string => {
  if (!dateStr) return '';

  const date = dayjs(dateStr);
  if (!date.isValid()) return '';

  return date.fromNow();
};

/**
 * Safely formats a date, handling invalid/epoch (1970) dates
 * @param date - The date to format (Date, string, number, null, or undefined)
 * @param format - The dayjs format string (default: 'MMM D, YYYY')
 * @param fallback - The fallback string for invalid dates (default: 'N/A')
 * @returns Formatted date string or fallback
 */
export const formatSafeDate = (
  date: Date | string | number | null | undefined,
  format = 'MMM D, YYYY',
  fallback = 'N/A'
): string => {
  if (!date) return fallback;
  const parsed = dayjs(date);
  // Check if date is valid and not epoch (1970) - dates before year 2000 are likely invalid
  if (!parsed.isValid() || parsed.year() < 2000) {
    return fallback;
  }
  return parsed.format(format);
};

export const convertTo24HourFormatUTC = (time12h: string): string => {
  const [time, modifier] = time12h.split(' ');
  const [hoursStr, minutes] = time.split(':');

  // Convert to 24-hour format first
  let hours = parseInt(hoursStr, 10);
  const mins = parseInt(minutes, 10);

  if (hours === 12) {
    hours = modifier === 'PM' ? 12 : 0;
  } else if (modifier === 'PM') {
    hours += 12;
  }

  // Create a date object with today's date and the specified time in local timezone
  const today = new Date();
  const localDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, mins);

  // Get UTC time
  const utcHours = localDate.getUTCHours().toString().padStart(2, '0');
  const utcMinutes = localDate.getUTCMinutes().toString().padStart(2, '0');

  return `${utcHours}:${utcMinutes}`;
};

export const convertUTCToLocal12Hour = (utcTime24h: string): string => {
  const [hoursStr, minutes] = utcTime24h.split(':');
  const utcHours = parseInt(hoursStr, 10);
  const utcMinutes = parseInt(minutes, 10);

  // Create a UTC date object with today's date and the specified UTC time
  const today = new Date();
  const utcDate = new Date(
    Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate(), utcHours, utcMinutes)
  );

  // Get local time
  const localHours = utcDate.getHours();
  const localMinutes = utcDate.getMinutes();

  // Convert to 12-hour format
  const period = localHours >= 12 ? 'PM' : 'AM';
  const displayHours = localHours === 0 ? 12 : localHours > 12 ? localHours - 12 : localHours;

  const formattedHours = displayHours.toString().padStart(2, '0');
  const formattedMinutes = localMinutes.toString().padStart(2, '0');

  return `${formattedHours}:${formattedMinutes} ${period}`;
};

/**
 * Formats feature value based on feature name with proper display names and postfix text
 * @param featureName - The name of the feature to format
 * @param quantity - The quantity/value of the feature
 * @param planType - The plan type (optional, used for conditional formatting)
 * @param isUnlimited - Whether the feature is unlimited
 * @param modelPlans - The model plans (optional, used for conditional formatting)
 * @returns Formatted feature string
 */
export const formatFeatureValue = (
  featureName: TFEATURE_NAMES,
  quantity: number,
  quantityLabel?: string,
  planType?: string,
  modelPlans?: IPlanModal[],
  isUnlimited = false
): string => {
  const displayName = FEATURE_DISPLAY_NAMES[featureName] || featureName;

  let formattedQuantity: string;

  switch (featureName) {
    case FEATURE_NAMES.MESSAGE_CREDIT:
      formattedQuantity = isUnlimited
        ? 'Unlimited'
        : planType === PLAN_TYPE.ENTERPRISE
          ? quantityLabel || ''
          : `${formatNumber(quantity)} / Month`;
      break;

    case FEATURE_NAMES.EXPERT_CHATBOTS:
      formattedQuantity = isUnlimited
        ? 'Unlimited'
        : planType === PLAN_TYPE.ENTERPRISE
          ? quantityLabel || ''
          : formatNumber(quantity);
      break;

    case FEATURE_NAMES.TEAM_SEATS:
      formattedQuantity = isUnlimited
        ? 'Unlimited'
        : planType === PLAN_TYPE.ENTERPRISE
          ? quantityLabel || ''
          : formatNumber(quantity);
      break;

    case FEATURE_NAMES.KNOWLEDGE_BASE:
      formattedQuantity = isUnlimited
        ? 'Unlimited'
        : planType === PLAN_TYPE.ENTERPRISE
          ? quantityLabel || ''
          : `${formatNumber(quantity)} Characters`;
      break;

    case FEATURE_NAMES.URL_CRAWLS:
      formattedQuantity = isUnlimited
        ? 'Unlimited'
        : planType === PLAN_TYPE.ENTERPRISE
          ? quantityLabel || ''
          : `${formatNumber(quantity)} Pages`;
      break;

    case FEATURE_NAMES.REMOVE_BRANDING:
      formattedQuantity = quantity ? 'Yes' : 'No';
      break;

    case FEATURE_NAMES.PRIORITY_EMAIL_SUPPORT:
      formattedQuantity = quantity ? 'Yes' : 'No';
      break;

    case FEATURE_NAMES.PRIVATE_CHATBOTS:
      formattedQuantity = quantity ? 'Unlimited' : 'No';
      break;

    case FEATURE_NAMES.SUBDOMAIN_ALLOWED:
      formattedQuantity = quantity ? 'Yes' : 'No';
      break;

    case FEATURE_NAMES.MODELS_ALLOWED:
      formattedQuantity = modelPlans?.length
        ? modelPlans.map((item) => item.model.name).join(', ')
        : planType === PLAN_TYPE.ENTERPRISE
          ? quantityLabel || ''
          : '-';
      break;

    case FEATURE_NAMES.PRIVACY:
      formattedQuantity =
        planType === PLAN_TYPE.ENTERPRISE ? quantityLabel || '' : quantity ? 'Yes' : 'No';
      break;

    case FEATURE_NAMES.BRANDING:
      formattedQuantity = planType === PLAN_TYPE.ENTERPRISE ? quantityLabel || '' : '';
      break;

    case FEATURE_NAMES.SUPPORT:
      formattedQuantity =
        planType === PLAN_TYPE.ENTERPRISE ? quantityLabel || '' : quantity ? 'Yes' : 'No';
      break;

    case FEATURE_NAMES.KEY_FEATURES:
      formattedQuantity = planType === PLAN_TYPE.ENTERPRISE ? quantityLabel || '' : '';
      break;

    case FEATURE_NAMES.DEDICATED_MANAGER:
      formattedQuantity = quantity ? 'Yes' : 'No';
      break;

    case FEATURE_NAMES.CHATBOTS:
      formattedQuantity = isUnlimited
        ? 'Unlimited'
        : planType === PLAN_TYPE.ENTERPRISE
          ? quantityLabel || ''
          : formatNumber(quantity);
      break;

    case FEATURE_NAMES.COMMUNITY_SUPPORT:
      formattedQuantity = quantity ? 'Yes' : 'No';
      break;

    case FEATURE_NAMES.EMAIL_SUPPORT:
      formattedQuantity = quantity ? 'Yes' : 'No';
      break;

    case FEATURE_NAMES.FILE_UPLOAD_LIMIT:
      formattedQuantity = isUnlimited ? 'Unlimited' : `${formatNumber(quantity)} MB`;
      break;

    default:
      formattedQuantity = isUnlimited ? 'Unlimited' : formatNumber(quantity);
      break;
  }

  return `${displayName}: ${formattedQuantity}`;
};

export const getCountryIcon = (countryCode: string): string | undefined =>
  countryFlags.find((c) => c.value === countryCode)?.icon;

/**
 * Removes square brackets and their content from a string
 * @param text - The text to clean
 * @returns The cleaned text without square brackets and their content
 */
export const removeSquareBrackets = (text: string): string => {
  if (!text) return '';
  return text.replace(/\[[^\]]*\]/g, '').trim();
};

export const playNotificationSound = (soundValue: string, isEnabled: boolean): void => {
  if (!isEnabled) return;

  // Default to notification-1 if no sound selected
  const effectiveSound = soundValue || 'notification-1';
  const soundOption = SOUND_OPTION_LIST.find((option) => option.value === effectiveSound);

  if (soundOption?.url) {
    const audio = new Audio(soundOption.url);
    audio.volume = 0.5;
    audio.play().catch((err) => console.error('Failed to play notification sound:', err));
  }
};

/**
 * Calculates the relative luminance of a hex color
 * Uses the W3C formula for relative luminance
 * @param hexColor - Hex color string (e.g., '#FFFFFF' or 'FFFFFF')
 * @returns Relative luminance value between 0 (black) and 1 (white)
 */
export const getLuminance = (hexColor: string): number => {
  // Remove # if present
  const hex = hexColor.replace('#', '');

  // Parse RGB values
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Apply gamma correction
  const rLinear = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gLinear = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bLinear = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

  // Calculate luminance
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Returns an appropriate contrast text color (white or dark) based on background color
 * Uses WCAG luminance formula to determine the best contrast
 * @param bgColor - Hex background color string (e.g., '#FFFFFF')
 * @param lightColor - Color to use for dark backgrounds (default: '#FFFFFF')
 * @param darkColor - Color to use for light backgrounds (default: '#1F2937')
 * @returns The appropriate text color for good contrast
 */
export const getContrastTextColor = (
  bgColor: string,
  lightColor = '#FFFFFF',
  darkColor = '#1F2937'
): string => {
  try {
    const luminance = getLuminance(bgColor);
    // Use 0.5 as threshold - higher luminance = lighter background = use dark text
    return luminance > 0.5 ? darkColor : lightColor;
  } catch {
    // Fallback to white if parsing fails
    return lightColor;
  }
};

/**
 * Build the Shopify Managed Pricing page URL for a merchant. The app is a
 * Managed Pricing app, so plan selection/approval is hosted by Shopify — we
 * redirect the merchant there instead of calling the Billing API (which
 * Shopify rejects for Managed Pricing apps).
 *
 * `shop` is the merchant's myshopify domain (e.g. "chat-dev-3.myshopify.com");
 * the store handle is the subdomain before ".myshopify.com".
 */
export const getShopifyManagedPricingUrl = (shop: string): string | null => {
  if (!shop) return null;
  const storeHandle = shop.replace(/\.myshopify\.com$/i, '').trim();
  if (!storeHandle) return null;
  const appHandle = process.env.NEXT_PUBLIC_SHOPIFY_APP_HANDLE || 'chat-co-ai-chatbot';
  return `https://admin.shopify.com/store/${storeHandle}/charges/${appHandle}/pricing_plans`;
};

/**
 * Shopify admin page where the merchant CANCELS — i.e. removes the app.
 *
 * Managed Pricing has no "cancel subscription" control: the plan-selection page
 * (getShopifyManagedPricingUrl above) only switches between plans, and we
 * publish no free plan to fall back to. Shopify's model is that uninstalling
 * the app ends the app subscription, so that is the cancellation path and this
 * is where it lives — "Apps and sales channels" in store settings.
 *
 * Pointing the Cancel action at the pricing_plans page instead is a dead end:
 * the merchant lands on four "Test with this plan" buttons and no way out.
 */
export const getShopifyAppUninstallUrl = (shop: string): string | null => {
  if (!shop) return null;
  const storeHandle = shop.replace(/\.myshopify\.com$/i, '').trim();
  if (!storeHandle) return null;
  return `https://admin.shopify.com/store/${storeHandle}/settings/apps/app_installations`;
};

/**
 * Resolve the Shopify store the *current* session belongs to.
 *
 * The managed-pricing / billing pages live inside a specific store's admin, so
 * the URL must target the store the merchant is actually signed into — NOT a
 * store arbitrarily picked from their linked installations. Using
 * `shopifyInstallations[0]` is order-dependent: when one Chat.co account is
 * linked to several shops (e.g. a shared demo account), `[0]` is the oldest
 * install, which sends the merchant to a store they can't access and Shopify
 * blocks with "you don't have permission to view this page".
 *
 * Preference order:
 *  1. `handoffShop` — the store captured during the Shopify dashboard handoff
 *     (from the App Bridge session token's `dest`). Only trusted when it is
 *     actually one of the account's installations, so a stale cookie from a
 *     previous login can never leak a wrong store.
 *  2. `shopifyInstallations[0]` — fallback for sessions with no handoff shop.
 */
export const getCurrentShopifyShop = (
  installations: Array<{ shop: string }> | undefined | null,
  handoffShop?: string | null
): string | null => {
  const shops = (installations ?? []).map((i) => i.shop).filter(Boolean);
  if (handoffShop && shops.includes(handoffShop)) {
    return handoffShop;
  }
  return shops[0] ?? null;
};

/**
 * Client idempotency key for operation-token executions (bulk delete /
 * retrain / schedule-delete). A retry that reuses the SAME key resumes the
 * server-side operation instead of failing or double-executing.
 */
export const makeIdempotencyKey = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);

const operationIdempotencyKeys = new Map<string, string>();

/**
 * ONE idempotency key per operation token (review round-3 condition 1).
 * The backend treats a same-token retry with a DIFFERENT key as a
 * conflicting replay (409), so minting a fresh key per click turned
 * "response was lost, user clicked again" into a misleading failure. Keyed
 * per operationId, so a retry resumes the durable operation (a COMPLETED
 * one returns its stored result) while distinct tokens keep distinct keys.
 * Tokens expire server-side in minutes; the map is cleared past a small
 * bound rather than tracking expiry.
 */
export const idempotencyKeyForOperation = (operationId: string): string => {
  const existing = operationIdempotencyKeys.get(operationId);
  if (existing) return existing;
  if (operationIdempotencyKeys.size >= 200) {
    operationIdempotencyKeys.clear();
  }
  const key = makeIdempotencyKey();
  operationIdempotencyKeys.set(operationId, key);
  return key;
};
